import PageHeader from "../components/PageHeader";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import FAQ from "../sections/FAQ";
import { PRICING } from "../data/content";
import { PillButton } from "../components/PillButton";
import { Check, ArrowUpRight } from "lucide-react";

const COMPARE = [
  { label: "Community feed & notices", starter: true, growth: true, ent: true },
  { label: "Complaint tracking", starter: true, growth: true, ent: true },
  { label: "Maintenance billing", starter: false, growth: true, ent: true },
  { label: "Online payments & reconciliation", starter: false, growth: true, ent: true },
  { label: "QR visitor & gate security", starter: false, growth: true, ent: true },
  { label: "Amenity booking & polls", starter: false, growth: true, ent: true },
  { label: "Multi-society dashboard", starter: false, growth: false, ent: true },
  { label: "Dedicated account manager", starter: false, growth: false, ent: true },
];

const Cell = ({ on }) =>
  on ? (
    <div className="w-6 h-6 rounded-full bg-[#0033A2] flex items-center justify-center mx-auto">
      <Check size={15} className="text-white" strokeWidth={3} />
    </div>
  ) : (
    <span className="text-black/25 font-bold">—</span>
  );

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <PageHeader
        overline="Pricing"
        title="Simple pricing,"
        highlight="serious value."
        subtitle="Start free, upgrade when you grow. No setup fees, no lock-in, and free migration from your current provider."
        testid="pricing-header"
      />

      {/* Plans */}
      <section className="pb-24">
        <div className="container-x">
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {PRICING.map((p, i) => (
              <StaggerItem key={p.name} className={p.featured ? "md:-mt-4 md:-mb-4" : ""}>
                <div
                  className={`h-full rounded-3xl border-2 border-black p-8 flex flex-col ${
                    p.featured
                      ? "bg-[#0033A2] text-white shadow-hard"
                      : "bg-white text-black shadow-hard-sm"
                  }`}
                  data-testid={`pricing-card-${p.name.toLowerCase()}`}
                >
                  {p.featured && (
                    <span className="self-start bg-white text-[#0033A2] font-heading font-bold text-xs uppercase tracking-widest rounded-full px-3 py-1 border-2 border-black mb-4">
                      Most popular
                    </span>
                  )}
                  <h3 className="font-heading font-extrabold text-2xl tracking-tight">{p.name}</h3>
                  <p className={`mt-2 font-medium ${p.featured ? "text-white/75" : "text-black/60"}`}>
                    {p.tagline}
                  </p>
                  <div className="mt-6 flex items-end gap-1">
                    <span className="font-heading font-black text-5xl tracking-tighter">{p.price}</span>
                    <span className={`mb-1.5 font-semibold ${p.featured ? "text-white/70" : "text-black/50"}`}>
                      {p.period}
                    </span>
                  </div>

                  <ul className="mt-8 space-y-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3">
                        <Check
                          size={20}
                          strokeWidth={3}
                          className={p.featured ? "text-white shrink-0 mt-0.5" : "text-[#0033A2] shrink-0 mt-0.5"}
                        />
                        <span className={`font-medium ${p.featured ? "text-white/90" : "text-black/75"}`}>
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    <PillButton
                      to="/contact"
                      variant={p.featured ? "white" : "primary"}
                      testid={`pricing-cta-${p.name.toLowerCase()}`}
                      className="w-full"
                    >
                      {p.cta} <ArrowUpRight size={18} strokeWidth={2.5} />
                    </PillButton>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Comparison table */}
      <section className="pb-24">
        <div className="container-x">
          <Reveal>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight mb-8">
              Compare every plan.
            </h2>
          </Reveal>
          <Reveal delay={0.05}>
            <div className="overflow-x-auto rounded-3xl border-2 border-black">
              <table className="w-full min-w-[640px]" data-testid="pricing-compare-table">
                <thead>
                  <tr className="bg-brand-light border-b-2 border-black">
                    <th className="text-left font-heading font-bold p-5 text-lg">Features</th>
                    <th className="font-heading font-bold p-5 text-lg">Starter</th>
                    <th className="font-heading font-bold p-5 text-lg text-[#0033A2]">Growth</th>
                    <th className="font-heading font-bold p-5 text-lg">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((row, i) => (
                    <tr
                      key={row.label}
                      className={`${i !== COMPARE.length - 1 ? "border-b border-black/15" : ""}`}
                    >
                      <td className="p-5 font-semibold text-black/80">{row.label}</td>
                      <td className="p-5 text-center"><Cell on={row.starter} /></td>
                      <td className="p-5 text-center bg-brand-light"><Cell on={row.growth} /></td>
                      <td className="p-5 text-center"><Cell on={row.ent} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      <FAQ />
    </div>
  );
}
